// Sprint 2 PR-D2 — Central Brain Quote Drafts view (read-only).
// Endpoint: GET /api/v2/quote-drafts
// Empty state baskın (production'da quote_drafts=0); CTA → WhatsApp Demo + sector=quote pre-fill.

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

export interface QuoteDraftRow {
  id: string;
  tenantId: string;
  leadId: string | null;
  conversationId: string | null;
  quoteType: string;
  items: unknown[];
  subtotal: string;
  tax: string;
  total: string;
  currency: string;
  notes: string | null;
  validityDays: number;
  approvalRequired: boolean;
  status: string;
  createdByAgent: string | null;
  agentRunId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteDraftsSummary { total: number; byStatus: Record<string, number>; byQuoteType: Record<string, number>; }
interface QuoteDraftsResponse { ok: true; quoteDrafts: QuoteDraftRow[]; summary: QuoteDraftsSummary; }
export interface QuoteDraftsFilters { status?: string; leadId?: string; agentRunId?: string; tenantId?: string; limit?: number; }

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; quoteDrafts: QuoteDraftRow[]; summary: QuoteDraftsSummary; filters: QuoteDraftsFilters }
  | { kind: 'error'; message: string; filters: QuoteDraftsFilters };

let state: ViewState = { kind: 'idle' };
let filters: QuoteDraftsFilters = { limit: 100 };

// WhatsApp Demo deeplink için module-level pre-fill flag (PR-D2: K-D2-2 URL state yok).
export let pendingSectorPrefill: string | null = null;
export function consumeSectorPrefill(): string | null {
  const v = pendingSectorPrefill;
  pendingSectorPrefill = null;
  return v;
}

export async function loadCbQuoteDrafts(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<QuoteDraftsResponse>('/api/v2/quote-drafts', filters as Record<string, string | number | undefined>);
  if (r.ok) state = { kind: 'ready', quoteDrafts: r.data.quoteDrafts, summary: r.data.summary, filters: { ...filters } };
  else state = { kind: 'error', message: r.message ?? r.error, filters: { ...filters } };
}

export function renderCbQuoteDrafts(): void {
  const root = document.getElementById('view-cb-quote-drafts');
  if (!root) return;
  root.innerHTML = renderCbQuoteDraftsHtml(state);
  attachHandlers();
}

export function renderCbQuoteDraftsHtml(s: ViewState): string {
  const f = (s.kind === 'ready' || s.kind === 'error') ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Quote Drafts</div>
        <div class="view-sub">central_brain.quote_drafts — read-only; gönderim Sprint 3</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${selectFilter('status', 'Status', f.status, ['draft','approval_required','approved','sent','rejected','expired'])}
      ${inputFilter('leadId', 'Lead ID', f.leadId)}
      ${inputFilter('tenantId', 'Tenant', f.tenantId)}
      <button id="cbqd-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  if (s.kind === 'error') return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  const { quoteDrafts, summary } = s;
  if (quoteDrafts.length === 0) {
    return `${header}
      <div class="rt-card" style="text-align:center;padding:32px;">
        <div style="font-size:48px;margin-bottom:8px;">📝</div>
        <div style="font-size:16px;font-weight:600;margin-bottom:6px;">Henüz quote draft yok</div>
        <div style="color:var(--muted);font-size:13px;margin-bottom:14px;">
          Beauty / project simulator çağrılarında quote draft üretilmez.<br>
          Quote draft için <strong>Quote sector'ünde</strong> simulator çalıştırın.
        </div>
        <button id="cbqd-cta-whatsapp-demo" class="btn-primary" style="padding:8px 16px;">WhatsApp Demo → Quote sector</button>
      </div>`;
  }
  return `${header}
    <div class="rt-card" style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;">
      <span><strong>${summary.total}</strong> total</span>
      ${chips('status', summary.byStatus)}
      ${chips('type', summary.byQuoteType)}
    </div>
    <div class="rt-card">
      <div class="rt-card-title">Quote Drafts (${quoteDrafts.length})</div>
      <table style="width:100%;font-size:13px;">
        <thead><tr>
          <th style="text-align:left;">ID</th><th style="text-align:left;">Lead</th>
          <th style="text-align:left;">Type</th><th style="text-align:right;">Total</th>
          <th style="text-align:left;">Status</th><th style="text-align:left;">Created</th>
        </tr></thead>
        <tbody>${quoteDrafts.map(q => `
          <tr>
            <td>${escHtml(q.id.slice(0,8))}</td>
            <td>${escHtml(q.leadId ?? '—')}</td>
            <td><code style="font-size:11px;">${escHtml(q.quoteType)}</code></td>
            <td style="text-align:right;">${escHtml(q.total)} ${escHtml(q.currency)}</td>
            <td>${escHtml(q.status)}</td>
            <td style="color:var(--muted);font-size:12px;">${escHtml(q.createdAt)}</td>
          </tr>
          ${Array.isArray(q.items) && q.items.length > 0 ? `<tr><td colspan="6" style="padding:0 0 12px 24px;">
            <details><summary style="cursor:pointer;font-size:11px;color:var(--muted);">items (${q.items.length})</summary>
              <pre style="background:var(--bg2);padding:8px;border-radius:4px;font-size:11px;white-space:pre-wrap;">${escHtml(JSON.stringify(q.items, null, 2))}</pre>
            </details>
          </td></tr>` : ''}`).join('')}
        </tbody>
      </table>
    </div>`;
}

function chips(label: string, m: Record<string, number>): string {
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${Object.entries(m).map(([k,v]) => `<span style="background:var(--bg2);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}
function selectFilter(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<select id="cbqd-f-${id}" class="form-select" style="width:140px;padding:4px 8px;"><option value="">(any)</option>${opts.map(o => `<option value="${escHtml(o)}"${o===value?' selected':''}>${escHtml(o)}</option>`).join('')}</select></label>`;
}
function inputFilter(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<input id="cbqd-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" /></label>`;
}
function attachHandlers(): void {
  const btn = document.getElementById('cbqd-apply');
  if (btn) {
    btn.addEventListener('click', async () => {
      const getV = (id: string) => (document.getElementById(`cbqd-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value || undefined;
      filters = { status: getV('status'), leadId: getV('leadId'), tenantId: getV('tenantId'), limit: 100 };
      await loadCbQuoteDrafts();
      renderCbQuoteDrafts();
    });
  }
  // CTA → WhatsApp Demo + sector=quote pre-fill
  const cta = document.getElementById('cbqd-cta-whatsapp-demo');
  if (cta) {
    cta.addEventListener('click', () => {
      pendingSectorPrefill = 'quote';
      // Nav item'a programatik click — main.ts router'ı page geçişi yapar
      document.getElementById('nav-whatsapp-demo')?.click();
    });
  }
}
